import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';
import { openAuthModal } from '@/store/slices/auth-modal.slice';
import {
	applyTradeIntent,
	buildAuthRedirectUrl,
	buildTradeIntentTarget,
	extractInviteToken,
	rememberInviteToken,
} from '@/utils';
import type { TradeIntent } from '@/utils';
import { useIsMobile } from '../generic/use-mobile.hook';

interface GuestAuthRedirectOptions {
	/**
	 * Replace the current history entry. For a page that sends guests away as soon as it
	 * loads: otherwise Back returns to that page, which redirects to sign-in again.
	 */
	replace?: boolean;
}

export function useGuestAuthRedirect() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isMobile = useIsMobile();
	const [, setSearchParams] = useSearchParams();

	return useCallback(
		(
			intent: TradeIntent = {},
			target?: string,
			{ replace = false }: GuestAuthRedirectOptions = {}
		) => {
			rememberInviteToken(extractInviteToken(target ?? window.location.search));
			if (isMobile) {
				if (Object.keys(intent).length > 0) {
					setSearchParams((prev) => applyTradeIntent(prev, intent), { replace: true });
				}
				dispatch(openAuthModal());
				return;
			}
			navigate(buildAuthRedirectUrl(buildTradeIntentTarget(intent, target)), { replace });
		},
		[isMobile, dispatch, navigate, setSearchParams]
	);
}
